import { useEffect, useState } from "react";
import { useSiteConfig } from "../../context/SiteConfigContext";
import AnimatedImage from "../../components/AnimatedImage";
import HeroImage from "../../components/HeroImage";
import SeoHead from "../../components/SeoHead";
import api from "../../lib/api";

export default function About() {
    const { config } = useSiteConfig();
    const [leaders, setLeaders] = useState([]);
    useEffect(() => { api.get("/leadership").then(({ data }) => setLeaders(data)).catch(() => {}); }, []);
    if (!config) return null;

    return (
        <div data-testid="about-page">
            <SeoHead pageKey="about" />
            {/* Hero */}
            <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
                <HeroImage
                    src={config.about_hero_image}
                    alt="About YACRRD"
                    animation={config.hero_animation || "ken-burns"}
                    overlay={config.hero_overlay_style || "dark"}
                    overlayOpacity={config.hero_overlay_opacity ?? 55}
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 w-full text-white">
                    <div className="text-xs uppercase tracking-[0.22em] mb-4">About YACRRD</div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
                        A youth-led answer to South Sudan's defining challenges.
                    </h1>
                </div>
            </section>

            {/* Intro */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">Who We Are</div>
                <div className="lg:col-span-10 prose-y">
                    <p className="text-xl leading-relaxed text-foreground max-w-4xl">{config.about_intro}</p>
                </div>
            </section>

            {/* Vision / Mission */}
            <section className="bg-muted/40 py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-8">
                    <div className="bg-card border border-border p-10 rounded-sm" data-testid="vision-card">
                        <div className="text-xs uppercase tracking-[0.22em] text-primary mb-4">Our Vision</div>
                        <p className="font-display text-3xl leading-tight">{config.about_vision}</p>
                    </div>
                    <div className="bg-card border border-border p-10 rounded-sm" data-testid="mission-card">
                        <div className="text-xs uppercase tracking-[0.22em] text-secondary mb-4">Our Mission</div>
                        <p className="font-display text-3xl leading-tight">{config.about_mission}</p>
                    </div>
                </div>
            </section>

            {/* Values + Objectives */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-16">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">Core Values</div>
                    <h2 className="font-display text-4xl mb-8">What guides us</h2>
                    <ul className="space-y-3" data-testid="values-list">
                        {(config.about_values || []).map((v, i) => (
                            <li key={i} className="flex items-start gap-3 border-l-2 border-primary pl-4 py-1">
                                <span className="text-base">{v}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">Strategic Objectives</div>
                    <h2 className="font-display text-4xl mb-8">Where we focus</h2>
                    <ol className="space-y-4 list-none" data-testid="objectives-list">
                        {(config.about_objectives || []).map((o, i) => (
                            <li key={i} className="flex items-start gap-4">
                                <span className="text-primary font-display text-2xl w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                <span className="text-base text-foreground/85 leading-relaxed">{o}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Why exists + Areas */}
            <section className="bg-muted/40 py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">Why YACRRD Exists</div>
                        <p className="font-display text-3xl leading-snug mb-6">{config.about_why_exists}</p>
                    </div>
                    <div className="lg:col-span-5 bg-card border border-border p-8 rounded-sm">
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">Areas of Operation</div>
                        <p className="text-base leading-relaxed">{config.about_areas_operation}</p>
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="leadership-section">
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">Leadership & Governance</div>
                <h2 className="font-display text-4xl sm:text-5xl mb-10">The team behind the mission</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {leaders.map((l) => (
                        <div key={l.id} className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`leader-card-${l.id}`}>
                            <div className="aspect-[4/5]">
                                <AnimatedImage src={l.image_url} alt={l.name} className="w-full h-full" />
                            </div>
                            <div className="p-5">
                                <h3 className="font-display text-xl mb-1">{l.name}</h3>
                                <div className="text-xs uppercase tracking-[0.18em] text-primary mb-3">{l.role}</div>
                                <p className="text-sm text-muted-foreground">{l.bio}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
